// General-Tab der Admin-Konsole: alle Konten, je mit Mülleimer zum Entfernen.
//
// Wird importiert von platform/shared/admin-konsole.js (renderGeneralPane).
// Nur für Rolle `admin` erreichbar; die Route prüft zusätzlich serverseitig.

import { authApi, ppLabel, ppHint } from './platform-api.js';

const CSS = `
/* Admin-Overlay — bewusst ein Overlay und kein Panel-Abschnitt: das Panel ist
   16rem breit, eine Userliste mit E-Mail und Tier-Toggle passt dort nicht
   hinein. Look wie die übrigen Overlays (#1a1a1a, 12px, weicher Schatten). */
.mednos-admin-backdrop {
  position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5);
  z-index: 10010; display: none;
}
.mednos-admin-backdrop.is-open { display: block; }
.mednos-admin {
  position: fixed; left: 50%; top: 50%; transform: translate(-50%, -50%);
  width: min(38rem, 92vw); max-height: 80vh; overflow-y: auto;
  padding: 20px; display: none; flex-direction: column; gap: 16px;
  background: #1a1a1a; color: #fff;
  border: none; border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  z-index: 10011;
  font-family: 'Inter', system-ui, sans-serif;
}
.mednos-admin.is-open { display: flex; }
.mednos-admin__head { display: flex; align-items: center; gap: 12px; }
.mednos-admin__title { font-size: 1rem; font-weight: 600; }
.mednos-admin__close {
  margin-left: auto; flex-shrink: 0;
  width: 1.6rem; height: 1.6rem; padding: 0;
  border: none; background: transparent; color: #fff;
  font-size: 1.1rem; line-height: 1; cursor: pointer; border-radius: 0.5rem;
  transition: opacity 0.15s;
}
.mednos-admin__close:hover { opacity: 0.65; }
.mednos-admin__section { display: flex; flex-direction: column; gap: 8px; }
.mednos-admin__row {
  display: flex; align-items: center; gap: 12px;
  padding: 8px 12px; border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
}
.mednos-admin__id { min-width: 0; display: flex; flex-direction: column; line-height: 1.3; }
.mednos-admin__name { font-size: 0.88rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mednos-admin__mail { font-size: 0.72rem; color: #b0b0b0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mednos-admin__actions { margin-left: auto; flex-shrink: 0; display: flex; gap: 6px; }
.mednos-admin__trash {
  width: 2rem; height: 2rem; padding: 0; display: grid; place-items: center;
  border: none; border-radius: 0.5rem; background: transparent; color: #b0b0b0;
  cursor: pointer; transition: color 0.15s, background 0.15s;
}
.mednos-admin__trash:hover { color: #ff6b6b; background: rgba(255, 107, 107, 0.1); }
.mednos-admin__trash svg { display: block; width: 1.1rem; height: 1.1rem; }
`;

function adminErrorText(status) {
  if (status === 401 || status === 403) return 'Keine Admin-Berechtigung (mehr).';
  return 'Admin-Daten derzeit nicht erreichbar.';
}

const TRASH_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16"/><path d="M10 11v6M14 11v6"/><path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12"/><path d="M9 7V4h6v3"/></svg>';

/** Mülleimer-Knopf. Das SVG ist eine Konstante, der Name geht per Attribut. */
function trashButton(username, onClick) {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'mednos-admin__trash';
  b.title = `${username} entfernen`;
  b.setAttribute('aria-label', `${username} entfernen`);
  b.innerHTML = TRASH_SVG;
  b.addEventListener('click', onClick);
  return b;
}

/** Eine Listenzeile. `actions` = fertige Elemente. Alles per textContent. */
function adminRow(name, sub, actions) {
  const row = document.createElement('div');
  row.className = 'mednos-admin__row';
  const id = document.createElement('span');
  id.className = 'mednos-admin__id';
  const nameEl = document.createElement('span');
  nameEl.className = 'mednos-admin__name';
  nameEl.textContent = name;
  const subEl = document.createElement('span');
  subEl.className = 'mednos-admin__mail';
  subEl.textContent = sub;
  id.append(nameEl, subEl);
  const acts = document.createElement('span');
  acts.className = 'mednos-admin__actions';
  acts.append(...actions);
  row.append(id, acts);
  return row;
}

async function renderUsersList(listEl) {
  const r = await authApi('/api/admin/users');
  if (r.status !== 200 || !Array.isArray(r.body)) {
    listEl.replaceChildren(ppHint(adminErrorText(r.status)));
    return;
  }
  if (!r.body.length) {
    listEl.replaceChildren(ppHint('Keine User.'));
    return;
  }
  listEl.replaceChildren(
    ...r.body.map((u) => {
      // Admin-Konten lehnt der Server ab — dann gar keinen Mülleimer zeigen.
      if (u.role === 'admin') return adminRow(`${u.username} — Admin`, u.email, []);
      return adminRow(u.username, u.email, [
        trashButton(u.username, async () => {
          if (!window.confirm(`${u.username} wirklich aus Mednos entfernen? Die Anmeldung ist danach gesperrt.`)) return;
          const r = await authApi(`/api/admin/users/${encodeURIComponent(u.id)}/entfernen`, { method: 'POST' });
          if (r.status !== 200) window.alert(r.body.error || adminErrorText(r.status));
          renderUsersList(listEl);
        }),
      ]);
    })
  );
}

/**
 * Baut den General-Tab der Admin-Konsole in `container` und startet das Laden.
 *
 * Ohne Rahmen: Backdrop, Kopfzeile und Schliessen-Knopf gehoeren der Konsole,
 * die alle Tabs traegt.
 */
export function renderGeneralPane(container) {
  const usersSection = document.createElement('div');
  usersSection.className = 'mednos-admin__section';
  const usersList = document.createElement('div');
  usersList.className = 'mednos-admin__section';
  usersSection.append(ppLabel('Alle User'), usersList);

  container.replaceChildren(usersSection);
  usersList.replaceChildren(ppHint('Lade…'));
  renderUsersList(usersList);
}

/** CSS dieses Moduls, damit die Konsole es einmalig mit einhaengen kann. */
export const ADMIN_CSS = CSS;
